<template>
  <div>
    <el-button
      type="primary"
      size="mini"
      @click="openDialog"
    >
      Assign Users
    </el-button>

    <el-dialog
      :visible.sync="dialog_visible"
      title="Supertask Assign Users to Job (Quote approved)"
      width="520px"
      @close="close_dialog"
      @opened="set_default_input(), fetch_job_lists()"
    >
      <el-form
        ref="form"
        label-width="120px"
        size="mini"
        x
      >
        <el-form-item
          label="Team Leader"
          class="badge"
        >
          <el-select
            v-model="selected_team_leader"
            placeholder="Select team leader"
            filterable
            clearable
            style="width: 100%"
            :disabled="!selected_list"
            @change="assign_team_leader"
          >
            <el-option
              v-for="user in team_leader_options"
              :key="user.id"
              :label="user.name"
              :value="user.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Select List"
          class="badge"
        >
          <el-select
            v-model="selected_list"
            placeholder="Select task list"
            filterable
            clearable
            style="width: 100%"
            @change="on_list_change"
          >
            <el-option
              v-for="list in lists"
              :key="list.id"
              :label="list.name"
              :value="list.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Suggested Users"
          size="mini"
          class="badge"
        >
          <el-select
            v-model="selected_suggested_users"
            placeholder="Select suggested users"
            filterable
            multiple
            collapse-tags
            clearable
            style="width: 100%"
            :disabled="!selected_list"
          >
            <el-option
              v-for="user in suggested_users"
              :key="user.id"
              :label="user.name"
              :value="user.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Assigned Users"
          class="badge"
        >
          <div class="assigned-user-badges">
            <el-tag
              v-for="user in assigned_users"
              :key="user.id"
              :type="is_team_leader(user.id) ? 'success' : 'info'"
              size="mini"
              closable
              :class="{ 'team-leader-tag': is_team_leader(user.id) }"
              style="margin: 4px"
              @close="delete_user_from_list(user.id)"
            >
              <span :class="{ 'team-leader-name': is_team_leader(user.id) }">
                {{ user.name }}
              </span>

              <span v-if="is_team_leader(user.id)">
                — Team Leader
              </span>
            </el-tag>
          </div>
        </el-form-item>

        <el-form-item class="badge">
          <el-button
            type="primary"
            icon="el-icon-plus"
            :disabled="!selected_list || selected_suggested_users.length === 0"
            @click="assign_users()"
          >
            Assign Selected Users
          </el-button>
        </el-form-item>
      </el-form>

      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          size="mini"
          @click="dialog_visible = false"
        >
          Cancel
        </el-button>

        <el-button
          type="primary"
          size="mini"
          @click="handle_ok"
        >
          OK
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { editor_store } from 'store/modules/editor';
import { mapGetters } from 'vuex';
import { set_default_input } from '../../utilities/DefaultInput';

export default {
  name: 'JobListUsers',

  store: editor_store,

  props: {
    proposal_id: {
      type: [Number, String],
      required: true
    },

    default_job_list_id: {
      type: [Number, String],
      required: false,
      default: null
    }
  },

  data() {
    return {
      dialog_visible: false,
      lists: [],
      selected_list: '',
      assigned_users: [],
      suggested_users: [],
      selected_suggested_users: [],
      selected_team_leader: '',
    };
  },

  computed: {
    ...mapGetters([
      'proposal',
    ]),

    team_leader_options() {
      const users = [
        ...this.assigned_users,
        ...this.suggested_users
      ];

      const unique_users = {};

      users.forEach(user => {
        if (user && user.id) {
          unique_users[user.id] = user;
        }
      });

      return Object.values(unique_users);
    }
  },

  watch: {
    proposal_id: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetch_job_lists();
        }
      }
    }
  },

  created() {
    if (this.proposal_id) {
      this.fetch_job_lists();
    }
  },

  methods: {
    set_default_input,

    is_team_leader(user_id) {
      return this.selected_team_leader &&
          user_id &&
          this.selected_team_leader.toString() === user_id.toString();
    },

    fetch_job_lists() {
      this.$http
        .get(`/api/job_lists/job_lists_by_proposal?proposal_id=${this.proposal_id}`)
        .then((response) => {
          this.lists = response.body || response.data || [];

          if (this.default_job_list_id) {
            this.selected_list = this.default_job_list_id;
          } else if (!this.selected_list && this.lists.length > 0) {
            this.selected_list = this.lists[0].id;
          }

          this.get_users_by_list();
        });
    },

    on_list_change() {
      this.assigned_users = [];
      this.suggested_users = [];
      this.selected_suggested_users = [];
      this.selected_team_leader = '';

      this.get_users_by_list();
    },

    get_users_by_list() {
      const list_id = this.selected_list;

      if (!list_id) {
        this.assigned_users = [];
        this.suggested_users = [];
        this.selected_team_leader = '';
        return;
      }

      this.$http
        .get(`/api/job_list_users/fetch_job_list_users?job_list_id=${list_id}`)
        .then(response => {
          this.assigned_users = response.body || response.data || [];
        });

      this.$http
        .get(`/api/job_list_users/fetch_job_list_suggested_users?job_list_id=${list_id}`)
        .then(response => {
          this.suggested_users = response.body || response.data || [];
        });

      this.fetch_team_leader();
    },

    fetch_team_leader() {
      if (!this.selected_list) {
        return;
      }

      this.$http
        .get(`/api/job_list_users/fetch_team_leader?job_list_id=${this.selected_list}`)
        .then(response => {
          const team_leader = response.body || response.data;

          this.selected_team_leader = team_leader && team_leader.id
            ? team_leader.id
            : '';
        });
    },

    delete_user_from_list(user_id) {
      if (!this.selected_list) {
        return;
      }

      this.$http
        .post('/api/job_list_users/unassign_user', {
          user_id: user_id,
          job_list_id: this.selected_list,
        })
        .then(() => {
          this.assigned_users = this.assigned_users.filter(
            user => user.id !== user_id
          );

          if (this.is_team_leader(user_id)) {
            this.selected_team_leader = '';
          }

          this.get_users_by_list();
          this.$emit('users-updated');
        });
    },

    assign_users() {
      const users_to_assign = this.selected_suggested_users;

      if (!this.selected_list || users_to_assign.length === 0) {
        return;
      }

      this.$http
        .post('/api/job_list_users/assign_users', {
          job_list_id: this.selected_list,
          user_ids: users_to_assign
        })
        .then(() => {
          this.selected_suggested_users = [];
          this.get_users_by_list();
          this.$emit('users-updated');
        });
    },

    assign_team_leader() {
      if (!this.selected_list || !this.selected_team_leader) {
        return;
      }

      this.$http
        .post('/api/job_list_users/assign_team_leader', {
          job_list_id: this.selected_list,
          user_id: this.selected_team_leader
        })
        .then(() => {
          this.get_users_by_list();
          this.$emit('users-updated');
        });
    },

    openDialog() {
      this.dialog_visible = true;
    },

    close_dialog() {
      this.dialog_visible = false;
    },

    handle_ok() {
      this.$emit('load_changes');
      this.close_dialog();
    },
  },
};
</script>

<style scoped>
.badge {
  margin-bottom: 10px;
}

.badge .item {
  margin-right: 10px;
}

.assigned-user-badges {
  min-height: 28px;
}

.team-leader-name {
  font-weight: 600;
}

.team-leader-tag {
  border-color: #67c23a;
}
</style>
